import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Idle } from '@ng-idle/core';
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { VERSION } from '../../../environments/version';
import { AuthenticationService } from '../../core/http/authentication.service';
import { LoginInfoService } from '../../core/http/login-info.service';
import { WebApiService } from '../../core/http/webapi.service';
import { LoginDto } from '../../core/models/login-dto.model';
import { LoginInfoDto } from '../../core/models/logininfo/login-info-dto.model';
import { SafeHtmlPipe } from '../../core/pipes/safe-html.pipe';
import { ApiService } from '../../core/services/api.service';
import { LocalStoreService } from '../../core/services/local-store.service';
import { Logger } from '../../core/services/logger.service';
import { LoaderComponent } from '../../shared/loader/loader.component';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, MdbFormsModule, SafeHtmlPipe, LoaderComponent],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  private readonly _router = inject(Router);
  private readonly _authenticationService = inject(AuthenticationService);
  private readonly _logger = inject(Logger);
  private readonly _localStoreService = inject(LocalStoreService);
  private readonly _idle = inject(Idle);
  private readonly _loginInfoService = inject(LoginInfoService);
  private readonly _webApiService = inject(WebApiService);
  private readonly _apiService = inject(ApiService);

  loginModel = signal<LoginDto>(new LoginDto());
  keepPassword = signal(false);
  errorMessage = signal('');
  versionShown = signal('');
  siteLoading = signal(false);
  loginInfo = signal<LoginInfoDto | null>(null);

  async ngOnInit(): Promise<void> {
    this.versionShown.set(VERSION.version);

    this._authenticationService.removeSessionData();
    this.loginModel.set(this._localStoreService.getData<LoginDto>('login_info') ?? new LoginDto());

    this.keepPassword.set(Boolean(this._localStoreService.getData<boolean>('keep_password') ?? false));

    // Zwecks Rückwärtskompatibilität
    if ((this.loginModel().password?.length ?? 0) > 0) {
      this.keepPassword.set(true);
    }

    try {
      this.loginInfo.set(await this._loginInfoService.getLoginInfo());
    } catch {
      this.loginInfo.set(null);
    }
  }

  pwChanged(): void {
    this.patchLoginModel({ passwordEncrypted: false });
  }

  updateLoginField(field: 'cloud' | 'user' | 'password', value: string): void {
    this.patchLoginModel({ [field]: value } as Partial<LoginDto>);
  }

  async onSubmit(): Promise<void> {
    this.siteLoading.set(true);
    this.errorMessage.set('');

    const loginModel = this.loginModel();
    if (loginModel.cloud === '' || loginModel.user === '' || loginModel.password === '') {
      if (loginModel.cloud === '') this.errorMessage.set('BauPlusCloud Name muss angegeben werden');
      else if (loginModel.user === '') this.errorMessage.set('Benutzername muss angegeben werden');
      else this.errorMessage.set('Passwort muss angegeben werden');

      this.siteLoading.set(false);
      return;
    }

    try {
      const webApiDto = await this._webApiService.getWebApi(loginModel.cloud);
      this._apiService.setApiUrl(webApiDto.url);
      this._apiService.setCloudType(webApiDto.cloudType);
    } catch {
      this.siteLoading.set(false);
      this.errorMessage.set(
        'Fehler bei der Abfrage, welche Cloud verwendet werden soll. Versuchen Sie es in einer Minute erneut. Bei wiederholten Problemen bitte den BauPlus Support kontaktieren.',
      );
      return;
    }

    try {
      const data = await firstValueFrom(this._authenticationService.login(this.loginModel()));

      if (!this.keepPassword()) {
        this.patchLoginModel({ password: '', passwordEncrypted: false });
      }
      this._localStoreService.setData('login_info', this.loginModel());
      if (this.keepPassword()) {
        this._localStoreService.setData('keep_password', 1);
      } else {
        this._localStoreService.remove('keep_password');
      }

      this.siteLoading.set(false);
      this._logger.log('Logged-In', data);
      this._idle.watch();
      await this._router.navigate(['/']);
    } catch (error: unknown) {
      this.siteLoading.set(false);
      this.handleLoginError(error);
      this._authenticationService.removeSessionData();
    }
  }

  private patchLoginModel(patch: Partial<LoginDto>): void {
    const nextModel = new LoginDto();
    Object.assign(nextModel, this.loginModel(), patch);
    this.loginModel.set(nextModel);
  }

  private handleLoginError(error: unknown): void {
    const httpError = error as HttpErrorResponse;
    let errVar = httpError.error;

    switch (httpError.status) {
      case 0:
        this.errorMessage.set(
          'WebApi-Endpoint nicht verfügbar. Versuchen Sie es in einer Minute erneut. Bei wiederholten Problemen bitte den BauPlus Support kontaktieren.',
        );
        return;
      case 401:
        // Von alter WebApi kommt der Wert als JSON-String
        if (typeof errVar === 'string') {
          try {
            errVar = JSON.parse(errVar);
          } catch {
            this.errorMessage.set(errVar);
            return;
          }
        }

        this.errorMessage.set(errVar.error);
        if (errVar.nextLoginAttempTime) {
          let message = this.errorMessage();
          if (!message.endsWith('.')) {
            message += '.';
          }
          const secondsRemaining = Math.ceil(moment(errVar.nextLoginAttempTime).diff(moment()) / 1000);
          message +=
            ' Falls Sie sich nicht korrekt mit Logout am System abgemeldet haben,' +
            ' ist der nächstmögliche Login-Zeitpunkt in ' +
            secondsRemaining +
            's.';
          this.errorMessage.set(message);
        }
        return;
      default:
        if (errVar?.exceptionMessage) {
          this.errorMessage.set(errVar.exceptionMessage);
        } else {
          this.errorMessage.set(
            'Interner Fehler beim Login. Bei wiederholten Problemen bitte den BauPlus Support kontaktieren.',
          );
        }
    }
  }
}
